
import {
  connectGitHubPat,
  disconnectGitHub,
  getGitHubSettings,
  startGitHubAuthorize,
} from "../api/settings.js";

type GitHubHost = HTMLElement & {
  configured: boolean;
  connected: boolean;
  status: string;
  error: string;
};

async function refreshGitHub(host: GitHubHost): Promise<void> {
  try {
    const settings = await getGitHubSettings();
    host.error = "";
    host.configured = settings.configured;
    host.connected = settings.connected;
    if (settings.connected) {
      const via = settings.auth_method === "pat" ? " via personal access token" : "";
      host.status = settings.login
        ? `Connected as @${settings.login}${via}.`
        : "GitHub is connected.";
    } else if (settings.configured) {
      host.status = "GitHub is not connected.";
    } else {
      host.status = "Connect a personal access token below to enable GitHub tools.";
    }
  } catch {
    host.error = "Failed to load GitHub settings.";
  }
}

async function connectWithPat(host: GitHubHost, form: HTMLFormElement): Promise<void> {
  const data = new FormData(form);
  const token = String(data.get("token") ?? "").trim();
  host.error = "";
  if (!token) {
    host.error = "Enter a personal access token.";
    return;
  }
  try {
    await connectGitHubPat(token);
    form.reset();
    await refreshGitHub(host);
  } catch (error) {
    host.error = error instanceof Error ? error.message : "Failed to connect GitHub.";
  }
}

async function connectGitHub(host: GitHubHost): Promise<void> {
  host.error = "";
  try {
    window.location.assign(await startGitHubAuthorize(`${window.location.pathname}${window.location.search}`));
  } catch {
    host.error = "Failed to start GitHub sign in.";
  }
}

const styles = css`
  .status-row {
    align-items: center;
    display: flex;
    flex-wrap: wrap;
    gap: 8px;
  }

  .status,
  .muted {
    color: var(--muted-foreground);
    font-size: 13px;
  }

  form {
    display: grid;
    gap: 10px;
  }

  label {
    color: var(--foreground);
    display: grid;
    font-size: 13px;
    gap: 6px;
  }

  input {
    background: var(--background);
    border: 1px solid var(--border);
    border-radius: 8px;
    color: var(--foreground);
    font: inherit;
    min-height: 34px;
    padding: 7px 9px;
  }

  .actions {
    display: flex;
    gap: 8px;
  }

  .error {
    color: var(--destructive);
    font-size: 13px;
    margin: 8px 0 0;
  }

  .error:empty {
    display: none;
  }
`;

export class AppSettingsGithub extends HTMLElement {
  static argonHydrate(element) {
    if (!(element instanceof AppSettingsGithub)) return;
    if (element.isConnected && !element.#bindingsMounted) {
      element.#syncHydratedProps();
      element.connectedCallback();
    }
    if (element.#root) element.#wireEvents(element.#root);
  }
  static observedAttributes = ["configured","data-configured","connected","data-connected","status","data-status","error","data-error"];

  #configured = false;
  #connected = false;
  #status = "Loading...";
  #error = "";
  #configuredSet = false;
  #connectedSet = false;
  #statusSet = false;
  #errorSet = false;
  #textStart0;
  #textEnd0;
  #textLast0;
  #textStart1;
  #textEnd1;
  #textLast1;
  #textStart2;
  #textEnd2;
  #textLast2;
  #textStart3;
  #textEnd3;
  #textLast3;
  #hooksLive = false;
  #cleanups = [];
  #root;
  #bindingsMounted = false;
  #hydrating = false;
  #isCSR = false;

  get configured() {
    return this.#configured;
  }

  set configured(value) {
    const raw = value ?? false;
    const next = raw == null ? raw : typeof raw === 'string' ? raw !== 'false' : Boolean(raw);
    if (Object.is(this.#configured, next)) return;
    this.#configured = next;
    this.#configuredSet = true;
    this.#updateBindings('configured');
  }

  get connected() {
    return this.#connected;
  }

  set connected(value) {
    const raw = value ?? false;
    const next = raw == null ? raw : typeof raw === 'string' ? raw !== 'false' : Boolean(raw);
    if (Object.is(this.#connected, next)) return;
    this.#connected = next;
    this.#connectedSet = true;
    this.#updateBindings('connected');
  }

  get status() {
    return this.#status;
  }

  set status(value) {
    const raw = value ?? "Loading...";
    const next = raw == null ? raw : raw;
    if (Object.is(this.#status, next)) return;
    this.#status = next;
    this.#statusSet = true;
    this.#updateBindings('status');
  }

  get error() {
    return this.#error;
  }

  set error(value) {
    const raw = value ?? "";
    const next = raw == null ? raw : raw;
    if (Object.is(this.#error, next)) return;
    this.#error = next;
    this.#errorSet = true;
    this.#updateBindings('error');
  }

  #syncHydratedProps() {
    if (this.#isCSR) return;
    if (this.hasAttribute('data-configured')) this.configured = this.dataset.configured;
    if (this.hasAttribute('data-connected')) this.connected = this.dataset.connected;
    if (this.hasAttribute('data-status')) this.status = this.dataset.status;
    if (this.hasAttribute('data-error')) this.error = this.dataset.error;
  }

  constructor(props) {
    super();
    if (props !== undefined) {
      this.#isCSR = true;
      this.configured = props.configured;
      this.connected = props.connected;
      this.status = props.status;
      this.error = props.error;
      this.#initState();
      const shadow = this.attachShadow({ mode: 'open' });
      shadow.innerHTML = this.#render();
      this.#mount(shadow, false);
    }
  }

  connectedCallback() {
    if (!this.#isCSR && !this.#bindingsMounted) {
      this.#syncHydratedProps();
    if (!this.#configuredSet) this.configured = this.dataset.configured;
    if (!this.#connectedSet) this.connected = this.dataset.connected;
    if (!this.#statusSet) this.status = this.dataset.status;
    if (!this.#errorSet) this.error = this.dataset.error;
    this.#initState();
    const shadow = this.shadowRoot;
    const root = shadow ?? this.attachShadow({ mode: 'open' });
    if (!shadow) root.innerHTML = this.#render();
    this.#mount(root, !!shadow);
    }
    if (this.#hooksLive) return;
    this.#hooksLive = true;
    {
      const cleanup = (() => {
    void refreshGitHub(this);
})();
      if (typeof cleanup === 'function') this.#cleanups.push(cleanup);
    }
  }

  disconnectedCallback() {
    this.#hooksLive = false;
    for (const cleanup of this.#cleanups.splice(0)) cleanup();
  }

  attributeChangedCallback(name, oldValue, newValue) {
    if (oldValue === newValue) return;
    switch (name) {
      case 'configured':
      case 'data-configured':
        this.configured = newValue;
        break;
      case 'connected':
      case 'data-connected':
        this.connected = newValue;
        break;
      case 'status':
      case 'data-status':
        this.status = newValue;
        break;
      case 'error':
      case 'data-error':
        this.error = newValue;
        break;
    }
  }

  #initState() {

  }

  #render() {
    
    return `<style>${this.#escText(styles)}</style><app-settings-section title="GitHub" description="Give your agents the official GitHub MCP tools for repositories, issues, and pull requests. Sign in with GitHub, or paste a personal access token if your server has no GitHub app configured."><div class="status-row"><!--argon-text:0-->${this.#connected ? "<app-badge>Connected</app-badge>" : "<app-badge variant=\"outline\">Not connected</app-badge>"}<!--/argon-text:0--><span class="status"><!--argon-text:1-->${this.#escText(this.#status)}<!--/argon-text:1--></span></div><!--argon-text:2-->${this.#connected ? `<div><app-button variant="outline" data-argon="3473">Disconnect</app-button></div>` : `${this.#configured ? `<div><app-button data-argon="3922">Sign in with GitHub</app-button></div>` : this.#escText("")}<form data-argon="4066"><label>Personal access token<input name="token" type="password" placeholder="ghp_... or github_pat_..." autocomplete="off"></label><p class="muted">Create a token with the scopes your agents need (for example <code>repo</code> and <code>read:org</code>) at github.com/settings/tokens. It is encrypted at rest and forwarded only to the GitHub MCP server.</p><div class="actions"><app-button type="submit">Connect with token</app-button></div></form>`}<!--/argon-text:2--><p class="error"><!--argon-text:3-->${this.#escText(this.#error)}<!--/argon-text:3--></p></app-settings-section>`;
  }



  #mount(root, hydrating) {
    this.#root = root;
    const walker = document.createTreeWalker(root, NodeFilter.SHOW_COMMENT);
    let marker;
    while ((marker = walker.nextNode())) {
      switch (marker.nodeValue) {
        case 'argon-text:0':
          this.#textStart0 = marker;
          break;
        case '/argon-text:0':
          this.#textEnd0 = marker;
          break;
        case 'argon-text:1':
          this.#textStart1 = marker;
          break;
        case '/argon-text:1':
          this.#textEnd1 = marker;
          break;
        case 'argon-text:2':
          this.#textStart2 = marker;
          break;
        case '/argon-text:2':
          this.#textEnd2 = marker;
          break;
        case 'argon-text:3':
          this.#textStart3 = marker;
          break;
        case '/argon-text:3':
          this.#textEnd3 = marker;
          break;
      }
    }
    this.#bindingsMounted = true;
    this.#hydrating = hydrating;
    this.#updateBindings();
    this.#hydrating = false;
    this.#wireEvents(root);
    if (!root.__argonEventObserver) {
      const observer = new window.MutationObserver(() => this.#wireEvents(root));
      observer.observe(root, { childList: true, subtree: true });
      Object.defineProperty(root, '__argonEventObserver', { value: observer });
    }
    this.setAttribute('hydrated', '');
    setTimeout(() => { if (this.#root === root) this.#wireEvents(root); }, 0);
  }

  #replaceRange(start, end, value) {
    if (!start || !end) return;
    const html = String(value);
    const sole = start.nextSibling;
    if (sole && sole !== end && sole.nextSibling === end && sole.nodeType === 3 && !/[<&]/.test(html)) {
      if (html.startsWith(sole.data)) sole.appendData(html.slice(sole.data.length));
      else sole.data = html;
      return;
    }
    const template = document.createElement('template');
    template.innerHTML = html;
    if (sole && sole !== end && sole.nextSibling === end && template.content.childNodes.length === 1 && this.#patchElement(sole, template.content.firstChild)) return;
    let oldNode = start.nextSibling;
    let newNode = template.content.firstChild;
    while (newNode) {
      const nextNew = newNode.nextSibling;
      if (!oldNode || oldNode === end) {
        end.before(newNode);
        if (newNode.nodeType === 1 || newNode.nodeType === 11) this.#wireEvents(newNode);
      } else {
        const nextOld = oldNode.nextSibling;
        if (!this.#patchNode(oldNode, newNode)) {
          oldNode.before(newNode);
          oldNode.remove();
          if (newNode.nodeType === 1 || newNode.nodeType === 11) this.#wireEvents(newNode);
        }
        oldNode = nextOld;
      }
      newNode = nextNew;
    }
    while (oldNode && oldNode !== end) {
      const nextOld = oldNode.nextSibling;
      oldNode.remove();
      oldNode = nextOld;
    }
  }

  #patchElement(oldEl, newEl) {
    if (!oldEl || !newEl || oldEl.nodeType !== 1 || newEl.nodeType !== 1 || oldEl.localName !== newEl.localName) return false;
    for (const attr of [...oldEl.attributes]) {
      if (attr.name !== 'hydrated' && !newEl.hasAttribute(attr.name)) oldEl.removeAttribute(attr.name);
    }
    for (const attr of [...newEl.attributes]) {
      if (oldEl.getAttribute(attr.name) !== attr.value) oldEl.setAttribute(attr.name, attr.value);
    }
    this.#patchChildren(oldEl, newEl);
    this.#wireEvents(oldEl);
    return true;
  }

  #patchNode(oldNode, newNode) {
    if (!oldNode || !newNode) return false;
    if (oldNode.nodeType === 3 && newNode.nodeType === 3) {
      if (oldNode.data !== newNode.data) oldNode.data = newNode.data;
      return true;
    }
    if (oldNode.nodeType === 8 && newNode.nodeType === 8) {
      if (oldNode.nodeValue !== newNode.nodeValue) oldNode.nodeValue = newNode.nodeValue;
      return true;
    }
    return this.#patchElement(oldNode, newNode);
  }

  #patchChildren(oldEl, newEl) {
    let oldNode = oldEl.firstChild;
    let newNode = newEl.firstChild;
    while (newNode) {
      const nextNew = newNode.nextSibling;
      if (!oldNode) {
        oldEl.append(newNode);
        if (newNode.nodeType === 1 || newNode.nodeType === 11) this.#wireEvents(newNode);
      } else {
        const nextOld = oldNode.nextSibling;
        if (!this.#patchNode(oldNode, newNode)) {
          oldEl.insertBefore(newNode, oldNode);
          oldNode.remove();
          if (newNode.nodeType === 1 || newNode.nodeType === 11) this.#wireEvents(newNode);
        }
        oldNode = nextOld;
      }
      newNode = nextNew;
    }
    while (oldNode) {
      const nextOld = oldNode.nextSibling;
      oldNode.remove();
      oldNode = nextOld;
    }
  }
  #escText(v) {
    return String(v).replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/>/g, '&gt;');
  }


  #eventTargets(scope, id) {
    const selector = `[data-argon="${id}"]`;
    const targets = [...scope.querySelectorAll(selector)];
    if (scope.nodeType === 1 && scope.matches(selector)) targets.unshift(scope);
    return targets;
  }

  #listen(el, id, event, handler) {
    const key = id + ':' + event;
    const root = this.#root;
    if (!root.__argonListeners) Object.defineProperty(root, '__argonListeners', { value: {} });
    const prev = root.__argonListeners[key];
    if (prev) root.removeEventListener(event, prev, true);
    const next = event => {
      const target = event.composedPath().find(node => node?.getAttribute?.('data-argon') === id);
      if (!target) return;
      const delegated = new Proxy(event, { get(source, prop) {
        if (prop === 'currentTarget') return target;
        const value = Reflect.get(source, prop, source);
        return typeof value === 'function' ? value.bind(source) : value;
      } });
      handler.call(target, delegated);
    };
    root.__argonListeners[key] = next;
    root.addEventListener(event, next, true);
  }

  #updateBindings(prop) {
    if (!this.#bindingsMounted) return;
    
    if (prop === undefined || prop === 'connected') {
      const __argonNext = `${this.#connected ? "<app-badge>Connected</app-badge>" : "<app-badge variant=\"outline\">Not connected</app-badge>"}`;
      if (__argonNext !== this.#textLast0) {
        this.#textLast0 = __argonNext;
        this.#replaceRange(this.#textStart0, this.#textEnd0, __argonNext);
      }
    }
    if (prop === undefined || prop === 'status') {
      const __argonNext = `${this.#escText(this.#status)}`;
      if (__argonNext !== this.#textLast1) {
        this.#textLast1 = __argonNext;
        this.#replaceRange(this.#textStart1, this.#textEnd1, __argonNext);
      }
    }
    if (prop === undefined || prop === 'connected' || prop === 'configured') {
      const __argonNext = `${this.#connected ? `<div><app-button variant="outline" data-argon="3473">Disconnect</app-button></div>` : `${this.#configured ? `<div><app-button data-argon="3922">Sign in with GitHub</app-button></div>` : this.#escText("")}<form data-argon="4066"><label>Personal access token<input name="token" type="password" placeholder="ghp_... or github_pat_..." autocomplete="off"></label><p class="muted">Create a token with the scopes your agents need (for example <code>repo</code> and <code>read:org</code>) at github.com/settings/tokens. It is encrypted at rest and forwarded only to the GitHub MCP server.</p><div class="actions"><app-button type="submit">Connect with token</app-button></div></form>`}`;
      if (__argonNext !== this.#textLast2) {
        this.#textLast2 = __argonNext;
        this.#replaceRange(this.#textStart2, this.#textEnd2, __argonNext);
      }
    }
    if (prop === undefined || prop === 'error') {
      const __argonNext = `${this.#escText(this.#error)}`;
      if (__argonNext !== this.#textLast3) {
        this.#textLast3 = __argonNext;
        this.#replaceRange(this.#textStart3, this.#textEnd3, __argonNext);
      }
    }
  }

  #wireEvents(scope) {
    const root = this.#root;
    this.#eventTargets(scope, '3473').forEach(el => this.#listen(el, '3473', 'click', () => {
    void disconnectGitHub()
        .then(() => refreshGitHub(this))
        .catch(() => {
        this.error = "Failed to disconnect GitHub.";
    });
}));
    this.#eventTargets(scope, '4066').forEach(el => this.#listen(el, '4066', 'submit', (event: Event) => {
    event.preventDefault();
    void connectWithPat(this, event.target as HTMLFormElement);
}));
    this.#eventTargets(scope, '3922').forEach(el => this.#listen(el, '3922', 'click', () => { void connectGitHub(this); }));
    const roots = [scope];
    for (let index = 0; index < roots.length; index++) {
      const candidates = roots[index].nodeType === 1 ? [roots[index], ...roots[index].querySelectorAll('*')] : [...roots[index].querySelectorAll('*')];
      for (const element of candidates) {
        customElements.upgrade(element);
        const hydrate = () => {
          customElements.upgrade(element);
          const definition = customElements.get(element.localName);
          definition?.argonHydrate?.(element);
        };
        if (element.isConnected && customElements.get(element.localName)) hydrate();
        else if (element.localName.includes('-')) customElements.whenDefined(element.localName).then(hydrate);
        else queueMicrotask(hydrate);
        if (element.shadowRoot) roots.push(element.shadowRoot);
      }
    }
  }
}
